import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  message,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiRequest } from "../../utils/apiClient";
import dayjs from "dayjs";
import { SearchOutlined, PictureOutlined } from "@ant-design/icons";
import checkPosType from "../../utils/posType";
import { SALES_TAX_RATES, UOM_OPTIONS, UOM_NUMBERS_PIECES_UNITS } from "../../utils/constants";

const { Title, Text } = Typography;

const Edit = () => {
  const { t } = useTranslation("pages");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState({});
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const searchInput = useRef(null);
  const [form] = Form.useForm();
  const user = useSelector((state) => state.user);
  const invoiceType = user?.invoiceType || "";
  const businessName = user?.businessName || "";
  const posType = checkPosType(businessName);
  const isDigital = invoiceType === "DIGITAL_INVOICING";

  const fetchProducts = async (page = 1, pageSize = 10, search = "") => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", pageSize);
      if (search) params.append("search", search);
      if (user?.role !== "cashier" && user?.selectedBranchId) {
        params.append("branchId", user.selectedBranchId);
      }

      const data = await apiRequest(
        `/api/products/get-all?${params.toString()}`,
        {
          method: "GET",
        }
      );

      // Ensure products is always an array for the table
      const allProducts = Array.isArray(data.products) ? data.products : [];

      // Filter out garbage products where title is 0 or 3
      const filteredProducts = allProducts.filter((product) => {
        const title = product?.title;
        return title !== 0 && title !== 3 && title !== "0" && title !== "3";
      });

      setProducts(filteredProducts);

      setPagination((prev) => ({
        ...prev,
        current: data.page || page,
        pageSize,
        total: data.total || 0,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(1, pagination.pageSize, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.selectedBranchId]);

  useEffect(() => {
    form.setFieldsValue(editingItem);
  }, [editingItem]);

  useEffect(() => {
    const getCategory = async () => {
      try {
        const effectiveBranchId = user?.role !== "cashier" ? user?.selectedBranchId : null;
        const url = effectiveBranchId
          ? `/api/categories/get-all?branchId=${encodeURIComponent(effectiveBranchId)}`
          : "/api/categories/get-all";
        const data = await apiRequest(url, { method: "GET" });
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.log(error);
      }
    };
    getCategory();
  }, [user?.selectedBranchId]);

  const handleSearch = (selectedKeys, dataIndex) => {
    const searchValue = selectedKeys[0] || "";
    setSearchText(searchValue);
    setSearchedColumn(dataIndex);
    // Always fetch first page on new search
    fetchProducts(1, pagination.pageSize, searchValue);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={t("searchColumn", { column: dataIndex })}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          {t("search")}
        </Button>
        <Button
          onClick={() => {
            clearFilters && clearFilters();
            setSearchText("");
            fetchProducts(1, pagination.pageSize, "");
          }}
          size="small"
          style={{ width: 90 }}
        >
          {t("reset")}
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const onFinish = async (values) => {
    try {
      const payload = {
        ...values,
        salesTax:
          Number(String(values.salesTax || "").replace("%", "").trim()) || 0,
        productId: editingItem._id,
      };

      await apiRequest("/api/products/update-product", {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      // Re-fetch current page to keep data in sync with the server
      await fetchProducts(pagination.current, pagination.pageSize, searchText);

      message.success(t("productUpdatedSuccess"));
      setIsEditModalOpen(false);
    } catch (error) {
      message.error(t("somethingWentWrong"));
    }
  };

  const deleteCategory = async (id) => {
    if (window.confirm(t("deleteConfirmProduct"))) {
      try {
        await apiRequest("/api/products/delete-product", {
          method: "DELETE",
          body: JSON.stringify({ productId: id }),
        });
        message.success(t("productDeletedSuccess"));
        const isLastItemOnPage = products.length === 1 && pagination.current > 1;
        const nextPage = isLastItemOnPage ? pagination.current - 1 : pagination.current;
        await fetchProducts(nextPage, pagination.pageSize, searchText);
      } catch (error) {
        message.error(t("somethingWentWrong"));
      }
    }
  };

  const renderWithNA = (value) => {
    return value === null || value === undefined || value === "" ? "NA" : value;
  };

  const columns = [
    {
      title: t("productName"),
      dataIndex: "title",
      width: "8%",
      ...getColumnSearchProps("title"),
      render: (_, record) => <p>{renderWithNA(record.title)}</p>,
    },
    {
      title: t("productImage"),
      dataIndex: "img",
      width: "4%",
      render: (_, record) => (
        record.img && String(record.img).trim() ? (
          <img
            src={record.img}
            alt={record.title || t("productImage")}
            className="w-full h-20 object-cover"
          />
        ) : (
          <div className="w-full h-20 flex items-center justify-center bg-gray-100 text-gray-400">
            <PictureOutlined style={{ fontSize: 24 }} />
          </div>
        )
      ),
    },
    {
      title: t("productPrice"),
      dataIndex: "price",
      width: "8%",
      render: (value) => renderWithNA(value),
    },
    {
      title: t("category"),
      dataIndex: "category",
      width: "8%",
      render: (value) => renderWithNA(value),
    },
    {
      title: t("hsCode"),
      dataIndex: "hscode",
      width: "6%",
      render: (value) => renderWithNA(value),
    },
    {
      title: t("totalStock"),
      dataIndex: "totalStock",
      width: "6%",
      render: (value) => renderWithNA(value),
    },
    ...(!isDigital && !posType
      ? [
          {
            title: "Mg",
            dataIndex: "mg",
            width: "6%",
            render: (value) => renderWithNA(value),
          },
          {
            title: t("barcode"),
            dataIndex: "barcode",
            width: "6%",
            render: (value) => renderWithNA(value),
          },
          {
            title: t("batchNumber"),
            dataIndex: "batchNumber",
            width: "6%",
            render: (value) => renderWithNA(value),
          },
          {
            title: t("expiry"),
            dataIndex: "expiry",
            width: "6%",
            render: (expiry) =>
              expiry ? dayjs(expiry).format("DD MMM YYYY") : "NA",
          },
        ]
      : []),
    {
      title: t("action"),
      dataIndex: "action",
      width: "8%",
      render: (_, record) => (
        <div>
          <Button
            type="text"
            className="px-2"
            onClick={() => {
              setIsEditModalOpen(true);
              setEditingItem({
                ...record,
                expiry: record.expiry ? dayjs(record.expiry) : null,
                salesTax:
                  typeof record.salesTax === "number"
                    ? `${record.salesTax}%`
                    : "0%",
              });
            }}
          >
            {t("editBtn")}
          </Button>
          <Button
            type="text"
            danger
            className="ml-2"
            onClick={() => deleteCategory(record._id)}
          >
            {t("delete")}
          </Button>
        </div>
      ),
    },
  ];


  // const columns = [
  //   {
  //     title: "Product Name",
  //     dataIndex: "title",
  //     width: "8%",
  //     render: (_, record) => <p>{record.title}</p>,
  //   },
  //   {
  //     title: "Product Image",
  //     dataIndex: "img",
  //     width: "4%",
  //     render: (_, record) => (
  //       <img src={record.img} alt="" className="w-full h-20 object-cover" />
  //     ),
  //   },
  //   {
  //     title: "Product Price",
  //     dataIndex: "price",
  //     width: "8%",
  //   },
  //   {
  //     title: "Category",
  //     dataIndex: "category",
  //     width: "8%",
  //   },
  //   {
  //     title: "HS Code",
  //     dataIndex: "hscode",
  //     width: "6%",
  //   },
  //   {
  //     title: "Total Stock",
  //     dataIndex: "totalStock",
  //     width: "6%",
  //   },
  //   {
  //     title: "Mg",
  //     dataIndex: "mg",
  //     width: "6%",
  //   },
  //   {
  //     title: "Barcode",
  //     dataIndex: "barcode",
  //     width: "6%",
  //   },
  //   {
  //     title: "Batch Number",
  //     dataIndex: "batchNumber",
  //     width: "6%",
  //   },
  //   {
  //     title: "Expiry",
  //     dataIndex: "expiry",
  //     width: "6%",
  //     render: (expiry) =>
  //       expiry ? dayjs(expiry).format("DD MMM YYYY") : "-",
  //   },
  //   {
  //     title: "Action",
  //     dataIndex: "action",
  //     width: "8%",
  //     render: (_, record) => (
  //       <div>
  //         <Button
  //           type="text"
  //           className="pl-0"
  //           onClick={() => {
  //             setIsEditModalOpen(true);
  //             setEditingItem({
  //               ...record,
  //               expiry: record.expiry ? dayjs(record.expiry) : null,
  //             });
  //           }}
  //         >
  //           Edit
  //         </Button>
  //         <Button type="text" danger onClick={() => deleteCategory(record._id)}>
  //           Delete
  //         </Button>
  //       </div>
  //     ),
  //   },
  // ];

  return (
    <>
      <Table
        bordered
        dataSource={products}
        columns={columns}
        rowKey={"_id"}
        loading={loading}
        scroll={{ x: 1200, y: 500 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          onChange: (page, pageSize) => {
            fetchProducts(page, pageSize, searchText);
          },
          showTotal: (total) => {
            const totalPages = Math.ceil(total / pagination.pageSize || 1);
            return t("totalItemsPages", { total, pages: totalPages });
          },
        }}
      />

      <Modal
        title={t("editProduct")}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={900}
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          initialValues={{ category: "All" }}
        >
          <Title level={4}>{t("mandatoryFields")}</Title>
          <Text type="secondary">
            {t("mandatoryFieldsDescEdit")}
          </Text>
          <Divider />

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={t("productName")}
                name="title"
                rules={[{ required: true, message: t("fieldRequired") }]}
              >
                <Input placeholder={t("enterProductName")} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={t("productPrice")}
                name="price"
                rules={[{ required: true, message: t("fieldRequired") }]}
              >
                <Input type="number" placeholder={t("enterProductPrice")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={t("unitOfMeasurement")}
                name="uom"
                rules={[{ required: true, message: t("uomRequired") }]}
              >
                <Select placeholder={t("selectUomShort")}>
                  {UOM_OPTIONS.map((uom) => (
                    <Select.Option key={uom} value={uom}>
                      {uom === UOM_NUMBERS_PIECES_UNITS
                        ? uom
                        : uom === "MT"
                        ? "Metric Ton (MT)"
                        : uom === "KG"
                        ? "Kilogram (KG)"
                        : uom === "Grams"
                        ? "Grams (G)"
                        : uom === "Packs"
                        ? "Packs"
                        : "Milligram (MG)"}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={t("hsCode")}
                name="hscode"
                rules={[{ required: true, message: t("hscodeRequired") }]}
              >
                <Input placeholder={t("enterHSCode")} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={t("totalStock")}
                name="totalStock"
                rules={[{ required: true, message: t("fieldRequired") }]}
              >
                <Input type="number" placeholder={t("enterTotalStock")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={t("salesTax")}
                name="salesTax"
                rules={[{ required: true, message: t("salesTaxRequired") }]}
              >
                <Select placeholder={t("selectTax")}>
                  {SALES_TAX_RATES.map((rate) => (
                    <Select.Option key={rate} value={rate}>
                      {rate}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={t("category")}
                name="category"
                rules={[{ required: true, message: t("fieldRequired") }]}
              >
                <Select
                  showSearch
                  placeholder={t("selectCategory")}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={[
                    { value: "All", label: t("all") },
                    ...categories.map((item) => ({
                      value: item.title,
                      label: item.title,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Title level={4}>{t("optionalFields")}</Title>
          <Text type="secondary">
            {t("optionalFieldsDescEdit")}
          </Text>
          <Divider />

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label={t("productImage")} name="img">
                <Input placeholder={t("enterProductImageUrl")} />
              </Form.Item>
            </Col>
          </Row>

          {!isDigital && !posType && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Mg" name="mg">
                    <Input type="number" placeholder={t("enterMg")} />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={t("expiryDate")} name="expiry">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD MMM YYYY"
                      placeholder={t("selectExpiryDate")}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={t("barcode")} name="barcode">
                    <Input placeholder={t("enterBarcode")} />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label={t("batchNumber")} name="batchNumber">
                    <Input placeholder={t("enterBatchNumber")} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Divider />
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">
              {t("updateProduct")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Edit;
